import 'api/init-api-interceptor';
import RedirectComponent from 'components/redirect-component';
import SpecialTestContainer from 'components/special-test-container';
import ClarityScript from 'components/thirt-party-scripts/ClarityScript';
import { useSyncConsentWithMida } from 'components/thirt-party-scripts/lib/use-sync-consent-with-mida';
import TagManagerScript from 'components/thirt-party-scripts/TagManagerScript';
import { config } from 'config';
import { useConfig } from 'config/use-config';
import { PdEnv } from 'constants/pd-env';
import { ShopId } from 'constants/shop';
import { CampaignSegmentProvider } from 'contexts/campaign-segment';
import { ExperienceTrackingContextProvider } from 'contexts/experience-tracking';
import GlobalTestHolder from 'contexts/global-test';
import { NinetailedAbTestProvider } from 'contexts/ninetailed-ab-test';
import NinetailedProviderWrapper, { STORAGE_KEY } from 'contexts/ninetailed-provider-wrapper';
import { ReferalProvider } from 'contexts/referal';
import RouteProvider from 'contexts/route';
import { SegmentationProvider } from 'contexts/segmentation';
import { SentryProvider } from 'contexts/sentry';
import { SpecialAbTestContextProvider } from 'contexts/special-ab-test-provider';
import { TrackingContextProvider } from 'contexts/tracking';
import { useSetActivePetFromQuery } from 'hooks/pet-profile/use-set-active-pet-from-query';
import { useSetPetFromQueryParams } from 'hooks/pet-profile/use-set-pet-from-query-params';
import ProductConfigurator from 'hooks/product/use-product-configurator';
import { useSyncPetTypeFromRoute } from 'hooks/use-sync-pet-type-from-route';
import 'intl-pluralrules';
import { CollectionProduct } from 'models/collection/collection';
import { useLockScrollWhenCookieBotActive } from 'modules/consent';
import { AppProps, NextWebVitalsMetric } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useRegion } from 'hooks/use-region';
import { FC, useEffect } from 'react';
import { useRootStore } from 'stores/use-root-store';
import { SWRConfig } from 'swr';
import 'tracking';
import { Gtm } from 'tracking';
import useGtmDebugger from 'tracking/hooks/useGtmDebugger';
import { getHreflangUrls } from 'utils/i18n/get-hreflang-urls';
import ErrorBoundary from '../components/error-boundary';
import PageProgress from '../components/page-progress';
import PageViewTrigger from '../components/page-view-trigger';
import { AuthContextProvider } from '../contexts/auth';
import { CartContextProvider } from '../contexts/cart';
import { CommonContextProvider } from '../contexts/common';
import { DomContextProvider } from '../contexts/dom';
import TranslationsProvider from '../contexts/translations';
import Layout from '../modules/layout';
import '../styles/tailwind.css';

const ShopSwitcherAsync = dynamic(() => import('../components/shop-switcher'), {
  ssr: false,
});

const CookieBannerAsync = dynamic(() => import('../components/cookie-banner'), {
  ssr: false,
});

const TrackingDebuggerAsync = dynamic(() => import('tracking/Debugger'), {
  ssr: false,
});

const ToastContainerAsync = dynamic(
  () => import('react-toastify').then((module) => module.ToastContainer),
  { ssr: false }
);

const App: FC<AppProps> = ({ pageProps, Component }) => {
  const { isLive, nodeEnv } = config;
  const { currencyCode, shopId, cookieDomain } = useConfig();
  const { asPath } = useRouter();
  const region = useRegion();
  const isGtmDebuggerEnabled = useGtmDebugger();
  const setCookieDomain = useRootStore((store) => store.setCookieDomain);

  useLockScrollWhenCookieBotActive();
  useSyncPetTypeFromRoute(); // sets pet type (cat or dog) based on the keywords in path
  useSetPetFromQueryParams(); // creates new pet profile based on query parameters
  useSetActivePetFromQuery(); // selects active pet profile if specific query provided

  // register cookieDomain with the pet-profile slice so its setters can write pd_has_{dog,cat}_profile
  // cookies for the edge middleware redirect (`src/middlewares/pet-profile-redirect.ts`).
  useEffect(() => {
    setCookieDomain(cookieDomain);
  }, [cookieDomain, setCookieDomain]);

  useSyncConsentWithMida();

  // Initialize collection product settings based on region
  CollectionProduct.setOptions({ currencyCode });
  const isLocalStorageAvailable =
    typeof window !== 'undefined'
      ? localStorage !== undefined && Boolean(localStorage.getItem(STORAGE_KEY))
      : false;

  return (
    <>
      <Head>
        {region &&
          getHreflangUrls({ asPath, region }).map(({ hreflang, href }) => (
            <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
          ))}
      </Head>
      <TranslationsProvider>
        <PageViewTrigger />
        <TagManagerScript />
        <ClarityScript />
        <ErrorBoundary>
          <NinetailedProviderWrapper
            previewEnabled={
              isLive === 'false' || (nodeEnv !== PdEnv.PRODUCTION && isLocalStorageAvailable)
            }
          >
            <SWRConfig
              value={{
                revalidateOnFocus: false,
                revalidateOnReconnect: false,
              }}
            >
              <NinetailedAbTestProvider>
                <ExperienceTrackingContextProvider>
                  <SpecialAbTestContextProvider>
                    <SpecialTestContainer />
                    <RedirectComponent />

                    <ReferalProvider>
                      <CommonContextProvider>
                        <PageProgress />
                        <ToastContainerAsync
                          position="bottom-right"
                          closeOnClick
                          pauseOnHover
                          draggable={false}
                        />
                        <DomContextProvider>
                          <SentryProvider>
                            <AuthContextProvider>
                              <SegmentationProvider>
                                <TrackingContextProvider>
                                  <CartContextProvider>
                                    <ProductConfigurator>
                                      <RouteProvider>
                                        <CampaignSegmentProvider>
                                          <Layout>
                                            <GlobalTestHolder>
                                              <Component {...pageProps} />
                                              {shopId === ShopId.CH && <CookieBannerAsync />}
                                              <ShopSwitcherAsync />
                                              {isGtmDebuggerEnabled && <TrackingDebuggerAsync />}
                                            </GlobalTestHolder>
                                          </Layout>
                                        </CampaignSegmentProvider>
                                      </RouteProvider>
                                    </ProductConfigurator>
                                  </CartContextProvider>
                                </TrackingContextProvider>
                              </SegmentationProvider>
                            </AuthContextProvider>
                          </SentryProvider>
                        </DomContextProvider>
                      </CommonContextProvider>
                    </ReferalProvider>
                  </SpecialAbTestContextProvider>
                </ExperienceTrackingContextProvider>
              </NinetailedAbTestProvider>
            </SWRConfig>
          </NinetailedProviderWrapper>
        </ErrorBoundary>
      </TranslationsProvider>
    </>
  );
};

export function reportWebVitals(metric: NextWebVitalsMetric): void {
  const { label, name, value, id } = metric;

  Gtm.track(
    {
      name:
        label === 'web-vital' ? Gtm.StandaloneEvent.WebVitals : Gtm.StandaloneEvent.NextJsMetric,
      properties: {
        event_category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
        value: Math.round(name === 'CLS' ? value * 1000 : value),
        event_label: id,
      },
    },
    { requiresSession: false }
  );
}

export default App;
